/**
 * Time, money and phone formatting (P2 §3.1). Everything renders in the TENANT's (or the location's) IANA time zone,
 * never the browser's: this is the one guard against the India IST-as-UTC class of bug. Money is integer cents → USD.
 */

let tenantTz = 'America/Chicago';
export const setTenantTz = (tz: string) => { tenantTz = tz || tenantTz; };
export const getTenantTz = () => tenantTz;

const cache = new Map<string, Intl.DateTimeFormat>();
function dtf(tz: string, o: Intl.DateTimeFormatOptions) {
  const k = tz + JSON.stringify(o);
  let f = cache.get(k);
  if (!f) { f = new Intl.DateTimeFormat('en-US', { timeZone: tz, ...o }); cache.set(k, f); }
  return f;
}
const d = (v: string | number | Date) => (v instanceof Date ? v : new Date(v));

/** "8:14 AM" */
export const fmtTime = (v: string | number | Date, tz = tenantTz) => dtf(tz, { hour: 'numeric', minute: '2-digit' }).format(d(v));
/** "Wed Oct 7" */
export const fmtDay = (v: string | number | Date, tz = tenantTz) => dtf(tz, { weekday: 'short', month: 'short', day: 'numeric' }).format(d(v)).replace(',', '');
/** "Wed 8:14 AM" */
export const fmtClock = (v: string | number | Date, tz = tenantTz) => dtf(tz, { weekday: 'short', hour: 'numeric', minute: '2-digit' }).format(d(v)).replace(',', '');
/** "Oct 7, 2026, 8:14 AM CDT" (with the zone, for history rows and DST-ambiguous times) */
export const fmtFull = (v: string | number | Date, tz = tenantTz) => dtf(tz, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(d(v));
/** Short zone name for the tenant right now, e.g. "CDT". */
export const tzAbbr = (tz = tenantTz, at: Date = new Date()) => dtf(tz, { timeZoneName: 'short' }).formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? '';
/** "7:30–8:30 AM" */
export function fmtWindow(start: string | null, end: string | null, tz = tenantTz) {
  if (!start) return 'Unscheduled';
  if (!end) return fmtTime(start, tz);
  return `${fmtTime(start, tz)}–${fmtTime(end, tz)}`;
}
/** YYYY-MM-DD of an instant in the tenant tz ("is this today for the business?"). */
export const localDate = (v: string | number | Date, tz = tenantTz) => dtf(tz, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d(v)).replace(/(\d+)\/(\d+)\/(\d+)/, '$3-$1-$2');
export const isToday = (v: string | number | Date, tz = tenantTz, now: Date = new Date()) => localDate(v, tz) === localDate(now, tz);

/** "3 min ago", "2 h ago", "Oct 4" */
export function ago(v: string | number | Date, now = Date.now()) {
  const s = Math.round((now - d(v).getTime()) / 1000);
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return fmtDay(v);
}

const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usd2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Integer cents → "$1,234" (or "$1,234.56" with cents:true; auto shows cents when not whole dollars). */
export function money(cents: number | null | undefined, o: { cents?: boolean } = {}) {
  if (cents == null) return '—';
  const v = cents / 100;
  return (o.cents ?? cents % 100 !== 0 ? usd2 : usd0).format(v);
}
export const dollarsToCents = (v: number) => Math.round(v * 100);

/**
 * NANP rules (US/CA), in-house: area code and exchange start 2-9 and the exchange isn't N11. The US product dials and
 * texts NANP numbers only (Telnyx US); anything else is shown verbatim in E.164.
 */
const NANP = /^([2-9]\d{2})([2-9]\d{2})(\d{4})$/;
const nanp = (ten: string) => { const m = NANP.exec(ten); return m && !/^[2-9]11$/.test(m[2]!) ? m : null; };

/** E.164 → "(512) 555-0109" for NANP numbers, the raw E.164 otherwise. */
export function phone(e164: string | null | undefined) {
  if (!e164) return '—';
  const d = e164.replace(/\D/g, '');
  const m = d.length === 11 && d.startsWith('1') ? nanp(d.slice(1)) : null;
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}
/** Loose user input ("512-555-0109", "(512) 555 0109", "+1 512…") → "+15125550109", or null if not a valid NANP number. */
export function toE164(input: string) {
  let d = input.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return d.length === 10 && nanp(d) ? `+1${d}` : null;
}

export const miles = (m: number | null | undefined) => (m == null ? '—' : `${m.toFixed(m < 10 ? 1 : 0)} mi`);
export const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '·';

/**
 * Wall-clock time in a zone → UTC ISO ("2026-11-01", "01:30", "America/Chicago"). DST: a skipped local time (spring
 * forward) returns null; an ambiguous one (fall back) resolves to the FIRST occurrence unless `later` is set.
 */
export function zonedToUtc(date: string, time: string, tz = tenantTz, later = false): string | null {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const guess = Date.UTC(y!, mo! - 1, d!, h!, mi!);
  const offsetAt = (ms: number) => {
    const p = dtf(tz, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(ms));
    const g = (t: string) => Number(p.find((x) => x.type === t)?.value);
    return Date.UTC(g('year'), g('month') - 1, g('day'), g('hour'), g('minute')) - ms;
  };
  const cands = [...new Set([offsetAt(guess - 864e5 / 2), offsetAt(guess + 864e5 / 2)])].map((o) => guess - o)
    .filter((ms) => { const o = offsetAt(ms); return ms + o === guess; }).sort((a, b) => a - b);
  if (!cands.length) return null;
  return new Date(later ? cands[cands.length - 1]! : cands[0]!).toISOString();
}
